import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';

import { withTheme } from 'styled-components';
import moment from 'moment';
import Box from 'core/components/Box';
import Button from 'core/components/Button';
import Flex, { FlexColumn } from 'core/components/Flex';
import DrawerSection from 'core/components/DrawerSection';
import Heading from 'core/components/Heading';
import ButtonLink from 'core/components/ButtonLink';
import Tag from 'core/components/Tag';
import Text from 'core/components/Text';
import CheckboxGroup from 'core/form/components/CheckboxGroup';
import LookbackDateRange from 'core/form/components/LookbackDateRange';
import DateRange from 'core/form/components/DateRange';
import Field from 'core/form/components/Field';
import FormComponent from 'core/form/components/FormComponent';
import MultiSelect from 'core/form/components/select/MultiSelect';
import Radio from 'core/form/components/Radio';
import RadioGroup from 'core/form/components/RadioGroup';

import AdminFilterItemRenderer from 'app/components/admin/AdminFilterItemRenderer';

const noop = () => {};

@inject('$app', '$exports')
@withRouter
@withTheme
@observer
class AdminFilterSidebar extends Component {
  static defaultProps = {
    title: 'Filters',
    formName: 'AdminFilterSidebar',
    showTitle: true,
    showClose: false,
    resetFiltersOnClear: false,
    useDiscreteFilters: false,
    updateHashOnFilterChange: true,
    filterValues: undefined,
    defaultValues: {},
    resetValues: {},
    onClose: noop,
    onFilterChange: noop,
    onFilterStateChange: noop,
    // additional filters that can be passed in that are too custom to be included into baseline form / field composition
    additionalFilters: [],
    groupTooltipRenderer: undefined
  };

  state = {
    groupDefaultOpen: {},
    fieldsUsingAndLogic: new Set()
  };

  form = undefined;

  debouncedUpdateHash = debounce((filters) => {
    const { $exports, updateHashOnFilterChange, externalHashHandler } = this.props;
    if (updateHashOnFilterChange) {
      $exports.setHash({ hashedFilters: filters });
    } else if (externalHashHandler) {
      externalHashHandler(filters);
    }
  }, 300);

  componentDidMount() {
    const { $exports, updateHashOnFilterChange } = this.props;

    $exports.getSettings().then(({ hashedFilters, groupDefaultOpen = {}, fieldsUsingAndLogic = [] }) => {
      if (this.form) {
        const { startDate, endDate, ...restHashedFilters } = hashedFilters || {};
        // If this view loaded with a ?q=xxx hash, then load hash filters into the form now
        if (hashedFilters && !isEqual(this.form.getValues(), hashedFilters)) {
          // Make sure we convert hashed dates into moment objects
          if (typeof startDate === 'string' || typeof startDate === 'number') {
            restHashedFilters.startDate = moment(startDate);
          }
          if (typeof endDate === 'string' || typeof endDate === 'number') {
            restHashedFilters.endDate = moment(endDate);
          }

          // Prefer lookback if we have it
          if (restHashedFilters.lookback) {
            restHashedFilters.startDate = moment().subtract(restHashedFilters.lookback, 'seconds');
            restHashedFilters.endDate = moment();
          }

          this.form.setValues(restHashedFilters);
        }

        this.setState({ groupDefaultOpen, fieldsUsingAndLogic: new Set(fieldsUsingAndLogic) }, () => {
          if (updateHashOnFilterChange) {
            // An if/else since setFilters also calls filterCollection
            this.setFilters(this.form, this.form.getValues());
          } else {
            this.filterCollection(this.form.getValues());
          }
        });
      }
    });
  }

  componentWillUnmount() {
    this.debouncedUpdateHash.cancel();
  }

  componentDidUpdate(prevProps) {
    const { filterValues, resetFiltersOnClear, onFilterChange, filterFields, onFilterStateChange, loading } =
      this.props;
    const { fieldsUsingAndLogic } = this.state;

    if (prevProps.filterValues && !filterValues && resetFiltersOnClear && this.form) {
      this.handleResetToDefault(this.form);
    } else {
      // Set form values and call the filter handler when the filter props are different than what's in the form
      // To make an even comparison, clear out undefined properties from each side so that {} will equal {a: undefined}
      let newFilters = omitBy(filterValues, (val) => val === undefined);
      const formValues = omitBy(this.form.getValues(), (val) => val === undefined);

      // merge formValues into newFilters to cover cases where filterValues was only a subset of possible filters
      newFilters = { ...formValues, ...newFilters };

      if (!isEqual(prevProps.filterFields, filterFields)) {
        onFilterStateChange(filterValues, filterFields, fieldsUsingAndLogic);
      }

      if (this.form && filterValues && (!isEqual(newFilters, formValues) || (prevProps.loading && !loading))) {
        this.form.setValues(filterValues);
        this.setFilters(this.form, this.form.getValues());
        onFilterChange(filterValues, this.form, filterFields);
      }
    }
  }

  filterCollection = (filters) => {
    const { useDiscreteFilters, collection, filterFields } = this.props;
    const { fieldsUsingAndLogic } = this.state;
    const discreteFilters = [];

    if (!useDiscreteFilters || !collection) {
      return;
    }

    Object.keys(filters).forEach((type) => {
      const field = filterFields.find((filterField) => filterField.name === type);

      if (field && field.filter && (!field.allowMultiSelect || filters[type]?.length > 0)) {
        const useAndLogic = field.canSwitchLogic && fieldsUsingAndLogic.has(type);
        discreteFilters.push({
          type,
          label: field.label,
          value: filters[type],
          fn: (model) => field.filter(model, filters[type], useAndLogic)
        });
      }
    });

    collection.setDiscreteFilters(discreteFilters);
    collection.filter();
  };

  filtersDifferFromDefault(form) {
    const { filterFields, resetValues } = this.props;
    const filters = form.getValues();

    return filterFields
      .filter((field) => !field.isDateRange && !field.hidden)
      .some((field) => {
        const value = filters[field.name];
        const resetValue = resetValues[field.name];

        // Do array check first - also check if list of items equals the default options
        if (Array.isArray(value)) {
          return !isEqual((resetValue || []).sort(), (value || []).sort());
        }

        // Ignore filter values that match reset values
        if (resetValue !== undefined && value === resetValue) {
          return false;
        }

        // Allow numbers (like 0), but check for falsy
        return Number.isFinite(value) || !!value;
      });
  }

  setFilters(form, filters, fieldName) {
    const { filterFields, collection, onFilterChange, onFilterStateChange } = this.props;
    const { fieldsUsingAndLogic } = this.state;

    if (collection) {
      this.filterCollection(filters);
    }

    onFilterStateChange(filters, filterFields, fieldsUsingAndLogic);
    onFilterChange(filters, form, filterFields, fieldName);
  }

  handleFilterChange = (form, field, value, filterField) => {
    const filters = form.getValues();

    if (filterField?.isDateRange && !filterField?.noLookback) {
      if (value.lookbackSeconds) {
        filters.lookback = value.lookbackSeconds;
        filters[filterField.startField] = null;
        filters[filterField.endField] = null;
      } else {
        filters.lookback = value.lookbackSeconds;
        filters[filterField.startField] = value[filterField.startField] * 1000;
        filters[filterField.endField] = value[filterField.endField] * 1000;
      }
    } else if (!Array.isArray(value) || (Array.isArray(value) && value.length > 0)) {
      filters[field.name] = value;
    } else {
      filters[field.name] = undefined;
    }

    this.setFilters(form, filters, field.name);
    this.debouncedUpdateHash(filters);

    if (filterField?.onChange) {
      filterField.onChange(field, value);
    }
  };

  handleResetToDefault = (form) => {
    const { $exports, externalHashHandler, filterFields, updateHashOnFilterChange, useDiscreteFilters, resetValues } =
      this.props;
    const resetOptions = {};

    filterFields.forEach((field) => {
      const { name, startField, endField } = field;

      if (field.isDateRange) {
        resetOptions[startField] = form.getValue(startField);
        resetOptions[endField] = form.getValue(endField);
      } else if (useDiscreteFilters) {
        resetOptions[name] = resetValues?.[name] ?? '';
      }
    });

    form.init(resetOptions);
    this.setFilters(form, resetOptions);

    if (updateHashOnFilterChange) {
      $exports.setHash({ hashedFilters: resetOptions });
    } else if (externalHashHandler) {
      externalHashHandler(resetOptions);
    }
  };

  handleClose = () => {
    const { onClose } = this.props;
    onClose();
  };

  handleGroupToggle = (group, isOpen) => {
    const { $exports } = this.props;
    const { groupDefaultOpen } = this.state;
    groupDefaultOpen[group] = isOpen;
    this.setState({ groupDefaultOpen }, () => {
      $exports.setHash({ groupDefaultOpen });
    });
  };

  handleLogicSwitch = (field, value) => {
    const { $exports, filterFields, onFilterChange, onFilterStateChange, useDiscreteFilters } = this.props;
    const { fieldsUsingAndLogic } = this.state;
    const newFieldsUsingAndLogic = new Set(fieldsUsingAndLogic);

    if (value === 'and') {
      newFieldsUsingAndLogic.add(field.name);
    } else {
      newFieldsUsingAndLogic.delete(field.name);
    }

    this.setState({ fieldsUsingAndLogic: newFieldsUsingAndLogic }, () => {
      const filters = this.form.getValues();
      this.filterCollection(filters);
      if (useDiscreteFilters) {
        onFilterChange(filters, this.form, filterFields);
      }
      onFilterStateChange(filters, filterFields, newFieldsUsingAndLogic);
      $exports.setHash({ fieldsUsingAndLogic: Array.from(newFieldsUsingAndLogic) });
    });
  };

  renderFilterField(form, filterField, idx) {
    const { filterFields, readOnly } = this.props;
    const { fieldsUsingAndLogic } = this.state;

    const {
      name,
      options,
      onQuery,
      allowMultiSelect,
      showCheckboxes,
      itemRenderer = AdminFilterItemRenderer,
      inline,
      customField,
      rules,
      helpText,
      isDateRange,
      noLookback,
      closeDateRangeOnSelection,
      startField,
      endField,
      label,
      hidden,
      canSwitchLogic,
      optionLimit,
      ...fieldProps
    } = filterField;

    if (hidden) {
      return null;
    }

    if (!customField && !isDateRange && (!options || options.length === 0)) {
      return null;
    }
    const ariaLabelId = `admin-filter-sidebar-${name}`;

    const isLastField = idx === filterFields.length - 1;

    if (isDateRange && noLookback) {
      return (
        <Box key={name} mb={isLastField ? 0 : '12px'}>
          {label && (
            <Text fontSize={14} as="div" fontWeight="bold" mb="4px" id={ariaLabelId}>
              {label}
            </Text>
          )}
          <DateRange
            {...fieldProps}
            form={form}
            allowSingleDayRange
            ariaLabelId={ariaLabelId}
            closeOnSelection={closeDateRangeOnSelection}
            startField={startField}
            endField={endField}
            onChange={(field, value) => this.handleFilterChange(form, field, value)}
            popoverProps={{ position: 'bottom-right' }}
          />
        </Box>
      );
    }

    if (isDateRange) {
      const startFormField = form.getValue(startField);
      const startValue = startFormField ? Math.floor(startFormField.valueOf() / 1000) : null;
      const endFormField = form.getValue(endField);
      const endValue = endFormField ? Math.floor(endFormField.valueOf() / 1000) : null;
      const lookbackSeconds = form.getValue('lookback')?.valueOf();

      return (
        <Box key={name} mb={isLastField ? 0 : '12px'}>
          {label && (
            <Text fontSize={14} as="div" fontWeight="bold" mb="4px">
              {label}
            </Text>
          )}
          <LookbackDateRange
            {...fieldProps}
            form={form}
            showLabel={false}
            stacked
            allowSingleDayRange
            closeOnSelection={closeDateRangeOnSelection}
            startField={startField}
            endField={endField}
            startDate={startValue}
            endDate={endValue}
            lookbackSeconds={lookbackSeconds}
            onChange={(datesOrLookback) =>
              this.handleFilterChange(form, { startField, endField }, datesOrLookback, filterField)
            }
          />
        </Box>
      );
    }

    let fieldComponent;

    if (customField) {
      fieldComponent = customField;
    } else if (!allowMultiSelect) {
      fieldComponent = <RadioGroup itemRenderer={itemRenderer} />;
    } else if (showCheckboxes) {
      fieldComponent = <CheckboxGroup itemRenderer={itemRenderer} inline={inline} />;
    } else {
      // optionLimit caps how many (filtered) options are rendered in the menu, for fields with very large option lists
      fieldComponent = <MultiSelect fill showFilter menuWidth="auto" optionLimit={optionLimit} />;
    }

    const baseFilterField = (
      <Field
        key={name}
        name={name}
        beforeOnChange={filterField?.beforeOnChange}
        onChange={(field, value) => this.handleFilterChange(form, field, value)}
        options={options}
        onQuery={onQuery}
        rules={rules}
        helperText={helpText}
        mb={isLastField ? 0 : '12px'}
        large
        disabled={fieldProps.disabled || readOnly}
        labelProps={{ mb: '4px' }}
        fieldStyle={fieldProps.fieldStyle}
        showLabel={canSwitchLogic ? false : undefined}
      >
        {fieldComponent}
      </Field>
    );

    if (canSwitchLogic) {
      const logic = fieldsUsingAndLogic.has(name) ? 'and' : 'or';
      return (
        <Box key={name}>
          <Flex alignItems="center" flexDirection="row" justifyContent="space-between" mb="4px">
            <Text fontSize={14} as="div" fontWeight="bold">
              {label}
            </Text>
            <Flex gap="12px" mb="-6px">
              <Radio
                checked={logic === 'and'}
                label="AND"
                onChange={() => this.handleLogicSwitch(filterField, 'and')}
              />
              <Radio checked={logic === 'or'} label="OR" onChange={() => this.handleLogicSwitch(filterField, 'or')} />
            </Flex>
          </Flex>
          {baseFilterField}
        </Box>
      );
    }

    return baseFilterField;
  }

  renderForm(form) {
    const {
      $app,
      filterFields,
      additionalFilters,
      group,
      title,
      showTitle,
      showClose,
      theme,
      readOnly,
      groupTooltipRenderer
    } = this.props;
    const { groupDefaultOpen } = this.state;

    if (!this.form) {
      this.form = form;
    }

    const groupedFilters = group
      ? filterFields.reduce((acc, filterField) => {
          if (!filterField.group) {
            return acc;
          }
          const groupIndex = acc.findIndex((grp) => grp.group === filterField.group);
          if (groupIndex === -1) {
            acc.push({ group: filterField.group, filters: [filterField] });
          } else {
            acc[groupIndex].filters.push(filterField);
          }
          return acc;
        }, [])
      : null;

    const ungroupedFilters = group ? filterFields.filter((filterField) => !filterField.group) : null;

    return (
      <>
        <Flex justifyContent="space-between" alignItems="flex-start">
          {showTitle && (
            <Flex
              flex={1}
              alignItems="center"
              justifyContent="space-between"
              p="7px 8px"
              borderBottom="thin"
              style={{
                background: theme.backgrounds.tableHeader
              }}
            >
              <Flex alignItems="center" flex="1 1 auto">
                <Heading level={5} fontWeight="heavy" mb={0}>
                  {title}
                </Heading>
                {!$app.isExport && this.filtersDifferFromDefault(form) && (
                  <Box textAlign="left" mt="1px" ml={1}>
                    <ButtonLink
                      key="clear"
                      intent="primary"
                      onClick={() => this.handleResetToDefault(form)}
                      minimal
                      small
                      disabled={readOnly}
                    >
                      Reset To Default
                    </ButtonLink>
                  </Box>
                )}
              </Flex>
              {showClose && !$app.isExport && (
                <Button
                  onClick={this.handleClose}
                  icon="chevron-left"
                  small
                  minimal
                  aria-expanded
                  ariaLabel="close filter"
                />
              )}
            </Flex>
          )}
        </Flex>

        <FlexColumn flex="1 1 0%" p="12px" overflow="auto" maxWidth="320px" bg="appBackground">
          {additionalFilters.map((extraField) => extraField)}
          {group ? (
            <>
              {ungroupedFilters.map((filterField, idx) => this.renderFilterField(form, filterField, idx))}
              {groupedFilters.map((grp) => {
                const { enabledCount, groupIsDisabled } = grp.filters.reduce(
                  (acc, filterField) => {
                    const value = form.getValues()[filterField.name];
                    const isOmittedDefault =
                      filterField.omitWhenDefault &&
                      filterField.defaultValue !== undefined &&
                      isEqual(value, filterField.defaultValue);

                    if (!filterField.disabled) {
                      acc.groupIsDisabled = false;
                    }

                    if (!value || isOmittedDefault) {
                      return acc;
                    }

                    if (Array.isArray(value)) {
                      acc.enabledCount += value.length > 0 ? 1 : 0;
                    } else {
                      acc.enabledCount += 1;
                    }
                    return acc;
                  },
                  { enabledCount: 0, groupIsDisabled: true }
                );
                return (
                  <Box key={grp.group} mb={3} opacity={groupIsDisabled ? 0.5 : 1}>
                    <DrawerSection
                      defaultOpen={groupIsDisabled ? false : groupDefaultOpen[grp.group]}
                      disabled={groupIsDisabled}
                      headerProps={{ borderBottom: 'none' }}
                      label={
                        <Flex alignItems="center" justifyContent="space-between">
                          <Flex gap={1}>
                            <Text large>{grp.group}</Text>
                            {groupTooltipRenderer && groupTooltipRenderer({ ...grp, enabledCount, groupIsDisabled })}
                          </Flex>
                          {enabledCount > 0 && <Tag round>{enabledCount}</Tag>}
                        </Flex>
                      }
                      onChange={(open) => this.handleGroupToggle(grp.group, open)}
                    >
                      {grp.filters.map((filterField, idx) => this.renderFilterField(form, filterField, idx))}
                    </DrawerSection>
                  </Box>
                );
              })}
            </>
          ) : (
            filterFields.map((filterField, idx) => this.renderFilterField(form, filterField, idx))
          )}
        </FlexColumn>
      </>
    );
  }

  render() {
    const { filterFields, defaultValues = {}, formName } = this.props;

    if (!Array.isArray(filterFields) || filterFields.length === 0) {
      return null;
    }

    const formFields = filterFields.reduce((acc, field) => {
      if (field.isDateRange) {
        acc[field.startField] = {
          defaultValue: defaultValues[field.startField] || field.startDefaultValue
        };
        acc[field.endField] = {
          defaultValue: defaultValues[field.endField] || field.endDefaultValue
        };
      } else {
        acc[field.name] = {
          label: field.label,
          placeholder: field.placeholder,
          defaultValue: defaultValues[field.name] || field.defaultValue,
          validateOptions: field.validateOptions !== false
        };
      }

      return acc;
    }, {});

    const formOptions = { name: formName };

    return (
      <FlexColumn width="100%" height="100%" overflow="hidden">
        <FormComponent fields={formFields} options={formOptions}>
          {({ form }) => this.renderForm(form)}
        </FormComponent>
      </FlexColumn>
    );
  }
}

export default AdminFilterSidebar;
